import React, { useMemo, useState } from 'react';
import Checkbox from 'material-ui/Checkbox';

const ABSENT = Symbol('absent');

function isContainer(value) {
    return value !== null && typeof value === 'object';
}

function canonicalize(value) {
    if (Array.isArray(value)) {
        return value.map(canonicalize);
    }
    if (isContainer(value)) {
        const out = {};
        Object.keys(value).sort().forEach((key) => {
            out[key] = canonicalize(value[key]);
        });
        return out;
    }
    return value;
}

function same(left, right) {
    return JSON.stringify(canonicalize(left)) === JSON.stringify(canonicalize(right));
}

function buildPresent(value, side) {
    if (Array.isArray(value)) {
        return {
            kind: 'container',
            container: 'array',
            left: side === 'left',
            right: side === 'right',
            children: value.map((item, index) => ({
                key: index,
                node: buildPresent(item, side),
            })),
        };
    }
    if (isContainer(value)) {
        return {
            kind: 'container',
            container: 'object',
            left: side === 'left',
            right: side === 'right',
            children: Object.keys(value).sort().map(key => ({
                key,
                node: buildPresent(value[key], side),
            })),
        };
    }
    return {
        kind: 'value',
        base: side === 'left' ? value : undefined,
        target: side === 'right' ? value : undefined,
        changed: true,
    };
}

function buildObject(base, target, hideSame) {
    const keys = Object.keys(base).concat(Object.keys(target)).filter((key, index, all) => all.indexOf(key) === index).sort();
    const children = [];
    keys.forEach((key) => {
        const child = buildNode(
            Object.prototype.hasOwnProperty.call(base, key) ? base[key] : ABSENT,
            Object.prototype.hasOwnProperty.call(target, key) ? target[key] : ABSENT,
            hideSame,
        );
        if (child) {
            children.push({ key, node: child });
        }
    });
    if (children.length === 0) {
        return null;
    }
    return {
        kind: 'container',
        container: 'object',
        left: true,
        right: true,
        children,
    };
}

function buildArray(base, target, hideSame) {
    const length = Math.max(base.length, target.length);
    const children = [];
    for (let index = 0; index < length; index += 1) {
        const child = buildNode(
            index < base.length ? base[index] : ABSENT,
            index < target.length ? target[index] : ABSENT,
            hideSame,
        );
        if (child) {
            children.push({ key: index, node: child });
        }
    }
    if (children.length === 0) {
        return null;
    }
    return {
        kind: 'container',
        container: 'array',
        left: true,
        right: true,
        children,
    };
}

function buildNode(base, target, hideSame) {
    const baseMissing = base === ABSENT;
    const targetMissing = target === ABSENT;
    if (baseMissing && targetMissing) {
        return null;
    }
    if (!baseMissing && !targetMissing && same(base, target)) {
        if (hideSame) {
            return null;
        }
        if (Array.isArray(base)) {
            return base.length === 0
                ? { kind: 'value', base, target, changed: false }
                : buildArray(base, target, false);
        }
        if (isContainer(base)) {
            return Object.keys(base).length === 0
                ? { kind: 'value', base, target, changed: false }
                : buildObject(base, target, false);
        }
        return { kind: 'value', base, target, changed: false };
    }
    if (!baseMissing && !targetMissing && Array.isArray(base) && Array.isArray(target)) {
        return buildArray(base, target, hideSame);
    }
    if (!baseMissing && !targetMissing && isContainer(base) && isContainer(target) && !Array.isArray(base) && !Array.isArray(target)) {
        return buildObject(base, target, hideSame);
    }
    if (baseMissing && isContainer(target)) {
        return buildPresent(target, 'right');
    }
    if (targetMissing && isContainer(base)) {
        return buildPresent(base, 'left');
    }
    return {
        kind: 'value',
        base: baseMissing ? undefined : base,
        target: targetMissing ? undefined : target,
        changed: true,
    };
}

function formatLines(value, indent, prefix) {
    const pretty = JSON.stringify(canonicalize(value), null, 2).split('\n');
    const pad = '  '.repeat(indent);
    return pretty.map((line, index) => (index === 0 ? `${pad}${prefix}${line}` : `${pad}${line}`));
}

function pairLines(leftLines, rightLines) {
    const count = Math.max(leftLines.length, rightLines.length, 1);
    const rows = [];
    for (let index = 0; index < count; index += 1) {
        rows.push({
            left: leftLines[index] || { text: '', tone: null },
            right: rightLines[index] || { text: '', tone: null },
        });
    }
    return rows;
}

function renderValue(node, indent, prefix) {
    const leftLines = node.base === undefined
        ? []
        : formatLines(node.base, indent, prefix).map(text => ({
            text,
            tone: node.changed ? 'del' : null,
        }));
    const rightLines = node.target === undefined
        ? []
        : formatLines(node.target, indent, prefix).map(text => ({
            text,
            tone: node.changed ? 'add' : null,
        }));
    return pairLines(leftLines, rightLines);
}

function addCommas(groups) {
    ['left', 'right'].forEach((side) => {
        const present = groups.filter(group => group.some(row => row[side].text !== ''));
        present.slice(0, -1).forEach((group) => {
            for (let index = group.length - 1; index >= 0; index -= 1) {
                if (group[index][side].text !== '') {
                    group[index][side] = {
                        ...group[index][side],
                        text: `${group[index][side].text},`,
                    };
                    break;
                }
            }
        });
    });
}

function renderNode(node, indent, prefix, path, collapsed) {
    if (node.kind === 'value') {
        return renderValue(node, indent, prefix);
    }
    const open = node.container === 'array' ? '[' : '{';
    const close = node.container === 'array' ? ']' : '}';
    const pad = '  '.repeat(indent);
    const isCollapsed = Boolean(collapsed[path]);
    const openText = isCollapsed ? `${open}…${close}` : open;
    const openRow = {
        left: node.left ? { text: `${pad}${prefix}${openText}`, tone: null } : { text: '', tone: null },
        right: node.right ? { text: `${pad}${prefix}${openText}`, tone: null } : { text: '', tone: null },
        togglePath: path,
        collapsed: isCollapsed,
    };
    if (isCollapsed) {
        return [openRow];
    }
    const groups = node.children.map((child) => {
        const childPrefix = node.container === 'object' ? `${JSON.stringify(child.key)}: ` : '';
        const childPath = `${path}/${String(child.key)}`;
        return renderNode(child.node, indent + 1, childPrefix, childPath, collapsed);
    });
    addCommas(groups);
    const closeRow = {
        left: node.left ? { text: `${pad}${close}`, tone: null } : { text: '', tone: null },
        right: node.right ? { text: `${pad}${close}`, tone: null } : { text: '', tone: null },
    };
    return [openRow].concat(...groups, [closeRow]);
}

function DiffCell({ cell, togglePath, collapsed, onToggle }) {
    const tone = cell.tone ? ` test-runs-diff-${cell.tone}` : '';
    return (
        <div className={`test-runs-diff-cell${tone}`}>
            {togglePath && cell.text ? (
                <button
                    type="button"
                    className="test-runs-diff-toggle"
                    aria-label={collapsed ? 'Expand' : 'Collapse'}
                    onClick={() => onToggle(togglePath)}
                >
                    {collapsed ? '▸' : '▾'}
                </button>
            ) : (
                <span className="test-runs-diff-gutter" />
            )}
            <span>{cell.text}</span>
        </div>
    );
}

export default function JsonDiff({ base, target, hideSame, onHideSame }) {
    const [collapsed, setCollapsed] = useState({});
    const tree = useMemo(
        () => buildNode(base === undefined ? ABSENT : base, target === undefined ? ABSENT : target, hideSame),
        [base, hideSame, target],
    );
    const rows = useMemo(
        () => (tree ? renderNode(tree, 0, '', '$', collapsed) : []),
        [collapsed, tree],
    );

    const onToggle = (path) => {
        setCollapsed(current => ({ ...current, [path]: !current[path] }));
    };

    return (
        <div>
            <Checkbox
                label="Hide the same fields"
                checked={hideSame}
                onCheck={(event, checked) => onHideSame(checked)}
                style={{ width: 'auto', marginBottom: 8 }}
            />
            {!tree ? (
                <p className="test-runs-hint">No differences</p>
            ) : (
                <div className="test-runs-diff">
                    <div className="test-runs-diff-row test-runs-diff-head">
                        <div className="test-runs-diff-cell">
                            <span className="test-runs-diff-gutter" />
                            Base
                        </div>
                        <div className="test-runs-diff-cell">
                            <span className="test-runs-diff-gutter" />
                            Target
                        </div>
                    </div>
                    {rows.map((row, index) => (
                        <div className="test-runs-diff-row" key={`${row.togglePath || 'line'}-${index}`}>
                            <DiffCell
                                cell={row.left}
                                togglePath={row.togglePath}
                                collapsed={row.collapsed}
                                onToggle={onToggle}
                            />
                            <DiffCell
                                cell={row.right}
                                togglePath={row.togglePath}
                                collapsed={row.collapsed}
                                onToggle={onToggle}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/JsonDiff.jsx